import { isGraphSquished } from '@/redux/selectors/dashboardSelectors';
import styled from '@emotion/styled';
import { Color, Skeleton, Tooltip, useTheme } from '@mui/material';
import { ChartData } from 'chart.js';
import { useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import Ellipsis from './Ellipsis';
import { BasicChartProps } from '../DashboardWidget';
import ChartLegend from './ChartLegend/ChartLegend';
import { chartMaxHeight, defaultChartAnimation } from './chartConfig';
import { NonMatrixedChartData, abbreviateNumber, buildChartColors, legendMouseLeave, legendMouseOver } from './chartUtils';
import { useAppSelector } from '@/redux/hooks';
export interface DonutChartData extends NonMatrixedChartData {
  totalLabel: string;
  legendPlacement: 'left' | 'right';
}

interface DonutChartProps extends BasicChartProps {
  data: DonutChartData;
}

const DonutChart = ({ data, title, colors: colorMap, loading = null, animation = defaultChartAnimation }: DonutChartProps) => {
  const SquishedSelector = isGraphSquished(data.id);
  const isSquished = useAppSelector(SquishedSelector);
  const donutRef = useRef();
  const theme = useTheme();
  const chartColors = buildChartColors(data.values.length, data.colors, colorMap);
  const total = data.values.reduce((acc, curr) => {
    acc += curr;
    return acc;
  }, 0);

  const getTooltip = (context: any) => {
    const total = chartData.datasets?.[0].data?.reduce((a: number, b: number) => a + b, 0);
    const percentage = ((context.raw / total) * 100).toFixed(1);
    return ` ${context.label}: ${percentage}%`;
  };

  // ChartJS Options
  const chartOptions = {
    cutout: '75%',
    layout: { padding: 10 },
    animation: animation,
    plugins: {
      tooltip: {
        callbacks: { label: getTooltip },
        backgroundColor: theme.palette.background.default,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.primary,
        displayColors: false
      },
      legend: { display: false }
    }
  };

  // ChartJS Data
  const chartData = {
    labels: data.labels,
    datasets: [
      {
        label: title,
        data: data.values,
        backgroundColor: ({ chart: { ctx } }: any) => {
          const gradients: Color[] = [];
          chartColors.forEach((item: any) => {
            const bg = ctx.createLinearGradient(0, 0, 225, 225);
            bg.addColorStop(0, item.start);
            bg.addColorStop(1, item.end);
            gradients.push(bg);
          });
          return gradients;
        },
        hoverBackgroundColor: chartColors.map((item: any) => item.hover[theme.palette.mode]),
        borderWidth: 0,
        margin: 13,
        hoverOffset: 0,
        borderRadius: 10,
        spacing: 6
      }
    ]
  };

  // build legend props to pass in - handles basic and complete styles
  const legendProps: any = {
    style: data.legend || 'basic'
  };

  if (legendProps.style !== 'complete') {
    legendProps.marker = 'value';
  }

  const HighlightContent = () => (
    <Highlight>
      <Total abbreviated={+(total >= 1000)}>{abbreviateNumber(total)}</Total>
      <TotalLabel>{data.totalLabel}</TotalLabel>
    </Highlight>
  );

  return (
    <Wrapper>
      <Widget placement={data.legendPlacement} id={`${title} reusable-donut-chart`} squished={+isSquished}>
        <Chart id="donut-chart">
          {loading ? (
            <SkeletonWrapper>
              <Skeleton variant="rectangular" width={chartMaxHeight} />
            </SkeletonWrapper>
          ) : (
            <ChartInner>
              <CenteredAbsolute>
                <Ellipsis />
                {total >= 1000 ? (
                  <Tooltip title={`${total.toLocaleString()} total`} placement="top">
                    <Highlight>
                      <Total abbreviated>{abbreviateNumber(total)}</Total>
                      <TotalLabel>{data.totalLabel}</TotalLabel>
                    </Highlight>
                  </Tooltip>
                ) : (
                  <Highlight>
                    <Total>{abbreviateNumber(total)}</Total>
                    <TotalLabel>{data.totalLabel}</TotalLabel>
                  </Highlight>
                )}
              </CenteredAbsolute>
              <DrawnDoughnut data={chartData as ChartData<'doughnut', unknown, unknown>} options={chartOptions} ref={donutRef} />
            </ChartInner>
          )}
        </Chart>
        <Details>
          <ChartLegend
            data={data}
            colors={chartColors}
            {...legendProps}
            skeletonOrientation="vertical"
            mouseOverCallback={(i: number) => legendMouseOver(donutRef, i)}
            mouseLeaveCallback={() => legendMouseLeave(donutRef)}
            loading={loading}
          />
        </Details>
      </Widget>
    </Wrapper>
  );
};

export default DonutChart;

// mobile base - breakpoit for everything above
const Highlight = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
  line-height: 1.33;
  z-index: 99;
  transition: transform 0.3s ease-out;

  &:hover {
    span:first-of-type {
      transform: scale(1.1);
    }
  }
`;

const Total = styled.span<{ abbreviated?: boolean }>`
  font-size: ${({ abbreviated }) => (abbreviated ? '3rem' : '4rem')};
  transition: transform 0.3s ease-out;
`;

const TotalLabel = styled.span`
  font-size: 1.1rem;
  letter-spacing: 0.75px;
  opacity: 0.4;
  transition: transform 0.3s ease-out;
`;

const Widget = styled.div<{ placement: DonutChartData['legendPlacement']; squished: number }>`
  width: 100%;
  margin: auto;
  margin: auto;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  row-gap: ${({ theme }) => theme.spacing(2)};
  column-gap: ${({ theme }) => theme.spacing(4)};

  @media (min-width: ${({ theme }) => theme.breakpoints.values['sm']}px) {
    flex-direction: ${({ placement, squished }) => {
      if (squished) return 'column';
      else if (placement === 'left') return 'row-reverse';
      else return 'row';
    }};
    flex-wrap: nowrap;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.values['md']}px) {
    flex-wrap: nowrap;
  }
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2)};
  margin: auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.values['md']}px) {
    flex-direction: row;
    flex-wrap: nowrap;
  }
`;

const Chart = styled.div`
  display: flex;
  flex-grow: 1;
  width: 100%;
  max-width: ${chartMaxHeight}px;
  height: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.values['md']}px) {
    max-width: ${chartMaxHeight - 50}px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.values['lg']}px) {
    max-width: ${chartMaxHeight}px;
  }
`;

const ChartInner = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  margin: auto;
  max-width: ${chartMaxHeight}px;
  padding-top: 10px;
  padding-left: 10px;
`;

const DrawnDoughnut = styled(Doughnut)`
  position: relative;
  z-index: 2;
`;

const CenteredAbsolute = styled.div`
  position: absolute;
  top: 5px;
  left: 5px;
  width: 100%;
  height: 100%;
`;

const Details = styled.div`
  flex-grow: 1;
`;

const SkeletonWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  .MuiSkeleton-root {
    padding-bottom: 100%;
    border-radius: 50%;
  }
`;
